import React, { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { ContentCopy as CopyIcon, Check as CheckIcon } from '@mui/icons-material';

const borderColor = '#30363d';

const syntaxHighlight = (json) => {
  if (typeof json !== 'string') {
    json = JSON.stringify(json, null, 2);
  }
  // escape HTML
  json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  return json.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g, (match) => {
    let color = '#c9d1d9'; // default
    let fontWeight = 'normal';

    if (/^"/.test(match)) {
      if (/:$/.test(match)) {
        // Key
        color = '#79c0ff'; // soft blue
        fontWeight = '500';
      } else {
        // String
        color = '#7ee787'; // light green
      }
    } else if (/true|false/.test(match)) {
      // Boolean
      color = '#58a6ff'; // cyan/blue
    } else if (/null/.test(match)) {
      // Null
      color = '#ff7b72'; // light red
    } else {
      // Number
      color = '#d29922'; // orange/yellow
    }
    return `<span style="color: ${color}; font-weight: ${fontWeight};">${match}</span>`;
  });
};

export const JsonViewer = ({ data, title = 'payload.json' }) => {
  const [copied, setCopied] = useState(false);
  const highlighted = syntaxHighlight(data);

  const handleCopy = () => {
    const rawJson = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
    navigator.clipboard
      .writeText(rawJson)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => console.error('Failed to copy text: ', err));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
      {/* Code Header Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, borderBottom: `1px solid ${borderColor}`, bgcolor: 'rgba(22, 27, 34, 0.4)' }}>
        <Typography variant='caption' sx={{ fontFamily: 'monospace', color: '#8b949e', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {title}
        </Typography>
        <IconButton
          onClick={handleCopy}
          size='small'
          sx={{
            color: copied ? '#3fb950' : '#8b949e',
            '&:hover': {
              color: copied ? '#3fb950' : '#e6edf3',
            },
            p: 0.5,
          }}
          title='Copy JSON'
        >
          {copied ? <CheckIcon fontSize='small' sx={{ width: 16, height: 16 }} /> : <CopyIcon fontSize='small' sx={{ width: 16, height: 16 }} />}
        </IconButton>
      </Box>
      {/* Code Pre Block */}
      <Box
        component='pre'
        sx={{
          m: 0,
          p: 2,
          color: '#c9d1d9',
          overflow: 'auto',
          fontSize: '13px',
          fontFamily: 'Consolas, "Liberation Mono", Menlo, Courier, monospace',
          flexGrow: 1,
          minHeight: 0,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
          backgroundColor: '#010409',
        }}
        dangerouslySetInnerHTML={{ __html: highlighted }}
      />
    </Box>
  );
};
